import React from 'react';
import { useTranslation } from 'react-i18next';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, format } from 'date-fns';
import { vi, enUS } from 'date-fns/locale';
import { CalendarEvent } from '../types';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils'; // Assuming Shadcn utils exist, or I can use template literals

interface CalendarGridProps {
    currentDate: Date;
    selectedDate: Date | null;
    onSelectDate: (date: Date) => void;
    getEventsForDate: (date: Date) => CalendarEvent[];
}

export const CalendarGrid: React.FC<CalendarGridProps> = ({
    currentDate, selectedDate, onSelectDate, getEventsForDate
}) => {
    const { t, i18n } = useTranslation('common');
    const locale = i18n.language === 'vi' ? vi : enUS;

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday start
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({
        start: startDate,
        end: endDate
    });

    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    // Or dynamic from locale? Simple array is robust enough for now if translated.
    // Let's use loop to get locale day names
    const weekDayNames = Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(2024, 0, i + 1); // 2024-01-01 was Monday
        return d.toLocaleDateString(i18n.language, { weekday: 'short' });
    });

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Week Headers */}
            <div className="grid grid-cols-7 border-b bg-gray-50">
                {weekDayNames.map((day, i) => (
                    <div key={i} className="py-3 text-center text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 auto-rows-[minmax(100px,auto)] divide-x divide-y">
                {days.map((day, dayIdx) => {
                    const events = getEventsForDate(day);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isCurrentMonth = isSameMonth(day, monthStart);
                    const isTodayDate = isToday(day);

                    return (
                        <div
                            key={day.toISOString()}
                            onClick={() => onSelectDate(day)}
                            className={`
                                relative p-2 min-h-[100px] cursor-pointer transition-colors group
                                ${!isCurrentMonth ? 'bg-gray-50/50 text-gray-400' : 'bg-white'}
                                ${isSelected ? 'ring-2 ring-inset ring-blue-500 z-10' : 'hover:bg-slate-50'}
                            `}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <span className={`
                                    flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium
                                    ${isTodayDate ? 'bg-blue-600 text-white shadow-md' : 'text-gray-700'}
                                    ${!isCurrentMonth && !isTodayDate ? 'text-gray-400' : ''}
                                `}>
                                    {format(day, 'd')}
                                </span>
                            </div>

                            <div className="space-y-1">
                                {events.map(event => (
                                    <div
                                        key={event.id}
                                        className={`
                                            px-2 py-1 text-xs rounded-md truncate font-medium border-l-2
                                            ${event.type === 'deadline' ? 'bg-red-50 text-red-700 border-red-500' :
                                                event.type === 'class' ? 'bg-indigo-50 text-indigo-700 border-indigo-500' :
                                                    'bg-emerald-50 text-emerald-700 border-emerald-500'}
                                        `}
                                        title={event.title}
                                    >
                                        {event.type === 'deadline' && '⚠️ '}
                                        {event.title}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
